import { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Loader2, ArrowLeft, Users, Building2, AlertTriangle, Star, TrendingUp,
  TrendingDown, Heart, Award,
} from "lucide-react";
import {
  fetchTenantEmployee, fetchTenantEmployees, getActiveTenant,
} from "../lib/tenantApi";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";

function TopBar({ tenant }) {
  const brand = tenant?.brand_color || "#0F172A";
  return (
    <div className="bg-white border-b border-slate-200 sticky top-0 z-30">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {tenant?.logo_url ? (
            <img src={tenant.logo_url} alt={tenant?.name} className="h-8 max-w-[140px] object-contain" />
          ) : (
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold" style={{ background: brand }}>
                {tenant?.name?.charAt(0) || "P"}
              </div>
              <div className="leading-tight">
                <div className="font-display font-semibold text-sm text-slate-900">{tenant?.name}</div>
                <div className="text-[0.6rem] uppercase tracking-wider text-slate-500">Yönetici Görünümü</div>
              </div>
            </div>
          )}
        </div>
        <Link to="/personas" className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded-md">
          <ArrowLeft className="w-3.5 h-3.5" /> Personalar
        </Link>
      </div>
    </div>
  );
}

function attritionRisk(emp) {
  // Simple heuristic: low engagement + low perf or new hire + low engagement
  const eng = emp.engagement_score || 100;
  const perf = emp.performance_rating || 3;
  const tenure = emp.tenure_years || 0;
  let score = 0;
  let reasons = [];
  if (eng < 50) { score += 35; reasons.push("Düşük bağlılık"); }
  else if (eng < 60) { score += 20; reasons.push("Orta bağlılık"); }
  if (perf < 2.5) { score += 30; reasons.push("Düşük performans"); }
  else if (perf < 3.2) { score += 15; reasons.push("Orta performans"); }
  if (tenure < 1) { score += 15; reasons.push("Kıdem < 1 yıl"); }
  if (emp.avg_sales_achievement_pct && emp.avg_sales_achievement_pct < 70) { score += 20; reasons.push("Hedef altı satış"); }
  return { score: Math.min(score, 100), reasons };
}

export default function RetailManagerView() {
  const { slug, empId } = useParams();
  const [manager, setManager] = useState(null);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();
  const tenant = getActiveTenant();

  useEffect(() => {
    fetchTenantEmployee(slug, empId).then(setManager);
    fetchTenantEmployees(slug, { limit: 500 }).then((d) => setTeam(d.items || [])).finally(() => setLoading(false));
  }, [slug, empId]);

  // Team = employees in same branch (excluding manager)
  const teamMembers = useMemo(
    () => team.filter((e) => e.branch_name === manager?.branch_name && e.id !== empId),
    [team, manager, empId],
  );

  const teamStats = useMemo(() => {
    if (!teamMembers.length) return null;
    const perfs = teamMembers.filter((e) => e.performance_rating).map((e) => e.performance_rating);
    const engs = teamMembers.filter((e) => e.engagement_score).map((e) => e.engagement_score);
    const sales = teamMembers.filter((e) => e.avg_sales_achievement_pct).map((e) => e.avg_sales_achievement_pct);
    return {
      headcount: teamMembers.length,
      talents: teamMembers.filter((e) => e.is_talent).length,
      avg_perf: perfs.length ? (perfs.reduce((a, b) => a + b, 0) / perfs.length) : null,
      avg_eng: engs.length ? Math.round(engs.reduce((a, b) => a + b, 0) / engs.length) : null,
      avg_sales: sales.length ? Math.round(sales.reduce((a, b) => a + b, 0) / sales.length) : null,
    };
  }, [teamMembers]);

  // Compute risks
  const ranked = useMemo(
    () => teamMembers
      .map((m) => ({ ...m, _risk: attritionRisk(m) }))
      .sort((a, b) => b._risk.score - a._risk.score),
    [teamMembers],
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <TopBar tenant={tenant} />
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      </div>
    );
  }

  if (!manager) {
    return (
      <div className="min-h-screen bg-slate-50">
        <TopBar tenant={tenant} />
        <div className="text-center py-20 text-sm text-slate-500">Yönetici bulunamadı</div>
      </div>
    );
  }

  const topRisks = ranked.filter((m) => m._risk.score >= 35).slice(0, 5);
  const topTalents = teamMembers.filter((m) => m.is_talent).slice(0, 5);

  return (
    <div className="min-h-screen bg-slate-50">
      <TopBar tenant={tenant} />
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Hero */}
        <div className="bg-white border border-slate-200 rounded-2xl p-7 mb-6">
          <div className="flex items-start gap-6">
            <Avatar className="w-24 h-24 ring-2 ring-slate-200">
              <AvatarImage src={manager.avatar} alt={manager.name} />
              <AvatarFallback>{manager.name?.charAt(0)}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h1 className="font-display text-3xl font-bold text-slate-900 mb-1">{manager.name}</h1>
              <div className="text-lg text-slate-700 mb-2">{manager.title}</div>
              <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                <span className="flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" />{manager.branch_name}</span>
                <span>{manager.region}</span>
                <span>Band <strong className="text-slate-900">{manager.salary_band}</strong></span>
                <span>Kıdem: {manager.tenure_years?.toFixed(1)} yıl</span>
              </div>
            </div>
          </div>
        </div>

        {/* KPI tiles */}
        {teamStats && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="text-[0.7rem] uppercase tracking-wider text-slate-500 font-semibold mb-1">Şube Kadrosu</div>
              <div className="font-display text-3xl font-bold text-slate-900">{teamStats.headcount}</div>
              <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                <Users className="w-3 h-3" /> Doğrudan ekip
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="text-[0.7rem] uppercase tracking-wider text-slate-500 font-semibold mb-1">Yetenek</div>
              <div className="font-display text-3xl font-bold text-amber-600">{teamStats.talents}</div>
              <div className="text-xs text-slate-500 mt-1">
                %{Math.round((teamStats.talents / teamStats.headcount) * 100)} havuzda
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="text-[0.7rem] uppercase tracking-wider text-slate-500 font-semibold mb-1">Ort. Performans</div>
              <div className="font-display text-3xl font-bold text-slate-900">{teamStats.avg_perf?.toFixed(1) || "—"}</div>
              <div className="text-xs text-slate-500 mt-1">1-5 ölçek</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="text-[0.7rem] uppercase tracking-wider text-slate-500 font-semibold mb-1">Bağlılık</div>
              <div className={`font-display text-3xl font-bold ${teamStats.avg_eng >= 70 ? "text-emerald-600" : teamStats.avg_eng >= 60 ? "text-amber-600" : "text-rose-600"}`}>
                {teamStats.avg_eng ? `%${teamStats.avg_eng}` : "—"}
              </div>
              <div className="text-xs text-slate-500 mt-1">Ekip ortalaması</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="text-[0.7rem] uppercase tracking-wider text-slate-500 font-semibold mb-1">Satış Başarısı</div>
              <div className="font-display text-3xl font-bold text-sky-600">{teamStats.avg_sales ? `%${teamStats.avg_sales}` : "—"}</div>
              <div className="text-xs text-slate-500 mt-1">Hedef üzeri</div>
            </div>
          </div>
        )}

        {/* Risks + Talents */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="risk-panel">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="w-4 h-4 text-rose-600" />
              <h3 className="font-display font-semibold text-slate-900">Ayrılma Risk Sinyalleri</h3>
              <span className="ml-auto text-xs text-slate-500">{topRisks.length} kişi</span>
            </div>
            {topRisks.length === 0 ? (
              <p className="text-xs text-emerald-600">Şu an yüksek riskli ekip üyesi yok.</p>
            ) : (
              <div className="space-y-2">
                {topRisks.map((m) => (
                  <div key={m.id} className="flex items-center gap-3 p-2 rounded-lg border border-rose-100 bg-rose-50/30" data-testid={`risk-row-${m.id}`}>
                    <Avatar className="w-9 h-9">
                      <AvatarImage src={m.avatar} alt={m.name} />
                      <AvatarFallback>{m.name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-slate-900 truncate">{m.name}</div>
                      <div className="text-[0.65rem] text-slate-500 truncate">
                        {m._risk.reasons.join(" · ")}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`font-display font-bold text-lg ${m._risk.score >= 60 ? "text-rose-600" : "text-amber-600"}`}>
                        %{m._risk.score}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="talents-panel">
            <div className="flex items-center gap-2 mb-4">
              <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
              <h3 className="font-display font-semibold text-slate-900">Yetenek Havuzu</h3>
              <span className="ml-auto text-xs text-slate-500">{teamStats?.talents || 0} kişi</span>
            </div>
            {topTalents.length === 0 ? (
              <p className="text-xs text-slate-400 italic">Bu şubede etiketli yetenek yok.</p>
            ) : (
              <div className="space-y-2">
                {topTalents.map((m) => (
                  <div key={m.id} className="flex items-center gap-3 p-2 rounded-lg border border-amber-100 bg-amber-50/30" data-testid={`talent-row-${m.id}`}>
                    <Avatar className="w-9 h-9">
                      <AvatarImage src={m.avatar} alt={m.name} />
                      <AvatarFallback>{m.name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-slate-900 truncate">{m.name}</div>
                      <div className="text-[0.65rem] text-slate-500">{m.title} · Band {m.salary_band}</div>
                    </div>
                    <div className="text-xs">
                      <div className="text-slate-700 font-mono">{m.performance_rating?.toFixed(1)}</div>
                      <div className="text-[0.6rem] text-slate-500">Perf.</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Team table */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="team-table">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <h3 className="font-display font-semibold text-slate-900 text-sm">Şube Ekibi</h3>
            <span className="text-xs text-slate-500">{teamMembers.length} kişi</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                <tr>
                  <th className="text-left px-4 py-3">Çalışan</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Pozisyon</th>
                  <th className="text-center px-4 py-3">Band</th>
                  <th className="text-center px-4 py-3">Perf.</th>
                  <th className="text-center px-4 py-3 hidden md:table-cell">Bağlılık</th>
                  <th className="text-center px-4 py-3 hidden lg:table-cell">Satış %</th>
                  <th className="text-center px-4 py-3">Risk</th>
                </tr>
              </thead>
              <tbody>
                {ranked.slice(0, 50).map((m) => (
                  <tr key={m.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`team-row-${m.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <Avatar className="w-8 h-8 ring-1 ring-slate-200">
                          <AvatarImage src={m.avatar} alt={m.name} />
                          <AvatarFallback>{m.name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div className="font-medium text-slate-900 flex items-center gap-1.5">
                          {m.name}
                          {m.is_talent && <Star className="w-3 h-3 fill-amber-500 text-amber-500" />}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-slate-700">{m.title}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-block px-2 py-0.5 rounded font-mono text-xs bg-slate-100 text-slate-700">{m.salary_band}</span>
                    </td>
                    <td className="px-4 py-3 text-center font-mono text-xs">
                      {m.performance_rating ? m.performance_rating.toFixed(1) : "—"}
                    </td>
                    <td className="px-4 py-3 text-center hidden md:table-cell text-xs">
                      {m.engagement_score ? `%${m.engagement_score}` : "—"}
                    </td>
                    <td className="px-4 py-3 text-center hidden lg:table-cell text-xs">
                      {m.avg_sales_achievement_pct ? `%${m.avg_sales_achievement_pct}` : "—"}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-block text-[0.65rem] px-2 py-0.5 rounded-full font-semibold ${
                        m._risk.score >= 60 ? "bg-rose-50 text-rose-700 border border-rose-200" :
                        m._risk.score >= 35 ? "bg-amber-50 text-amber-700 border border-amber-200" :
                        "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      }`}>
                        %{m._risk.score}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
